import type { MadeToOrderField, CustomResponse } from "@shared/schema";

// Shared renderer for a made-to-order listing's seller-defined custom fields.
// Used on the product detail page and at checkout so the buyer answers the
// same questions consistently. The seller defines the fields; the buyer's
// answers are CustomResponse[] keyed by fieldId.

export function madeToOrderResponsesComplete(
  fields: MadeToOrderField[] | null | undefined,
  responses: CustomResponse[] | undefined,
): boolean {
  if (!fields || fields.length === 0) return true;
  for (const f of fields) {
    if (!f.required) continue;
    const r = responses?.find((x) => x.fieldId === f.id);
    if (!r) return false;
    if (Array.isArray(r.value)) {
      if (r.value.length === 0) return false;
    } else if (!r.value.trim()) {
      return false;
    }
  }
  return true;
}

function valueFor(
  responses: CustomResponse[],
  fieldId: string,
): string | string[] | undefined {
  return responses.find((r) => r.fieldId === fieldId)?.value;
}

export function MadeToOrderFields({
  fields,
  responses,
  onChange,
  onCommit,
}: {
  fields: MadeToOrderField[];
  responses: CustomResponse[];
  onChange: (next: CustomResponse[]) => void;
  // Called when an edit "commits" (blur for text, change for select/checkbox)
  // so callers like checkout can refresh a payment intent without churning on
  // every keystroke. Optional.
  onCommit?: () => void;
}) {
  const setValue = (field: MadeToOrderField, value: string | string[]) => {
    const next = responses.filter((r) => r.fieldId !== field.id);
    next.push({ fieldId: field.id, label: field.label, value });
    onChange(next);
  };

  const toggle = (field: MadeToOrderField, opt: string, checked: boolean) => {
    const cur = (valueFor(responses, field.id) as string[] | undefined) ?? [];
    setValue(field, checked ? [...cur, opt] : cur.filter((o) => o !== opt));
    onCommit?.();
  };

  return (
    <div className="space-y-3">
      {fields.map((f) => {
        const val = valueFor(responses, f.id);
        return (
          <div key={f.id} className="space-y-1">
            <label className="block text-xs font-semibold text-foreground">
              {f.label}
              {f.required && <span className="text-destructive"> *</span>}
            </label>

            {f.type === "text" && (
              <input
                value={(val as string) ?? ""}
                onChange={(e) => setValue(f, e.target.value)}
                onBlur={() => onCommit?.()}
                className="w-full rounded-lg border border-card-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            )}

            {f.type === "paragraph" && (
              <textarea
                value={(val as string) ?? ""}
                onChange={(e) => setValue(f, e.target.value)}
                onBlur={() => onCommit?.()}
                rows={3}
                className="w-full rounded-lg border border-card-border bg-background px-3 py-2 text-sm leading-6 outline-none focus:border-primary"
              />
            )}

            {f.type === "select" && (
              <select
                value={(val as string) ?? ""}
                onChange={(e) => {
                  setValue(f, e.target.value);
                  onCommit?.();
                }}
                className="w-full rounded-lg border border-card-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value="">Choose…</option>
                {(f.options ?? []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}

            {f.type === "checkbox" && (
              <div className="space-y-1">
                {(f.options ?? []).map((opt) => {
                  const arr = (val as string[] | undefined) ?? [];
                  return (
                    <label key={opt} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={arr.includes(opt)}
                        onChange={(e) => toggle(f, opt, e.target.checked)}
                        className="h-4 w-4 accent-primary"
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
